'use client';

import type { Basket } from '@libs/common-next';
import { getCartCount, useBasketCount, usePageLayout } from '@libs/common-next';
import type { Nullable } from '@libs/core';

import type {
    HandleAddToCart,
    ProductData,
} from '@app/pdp/components/AddToCart/DeliveryMethods/types';
import { ITEM_NOT_AVAILALBLE_FAULT_TYPE } from '@libs/ocapi';

type UseAddToCart = {
    handleAddToCartAction: (body?: ProductData) => Promise<Nullable<Basket>>;
};

export const useAddToCart = (
    handleAddToCart?: HandleAddToCart
): UseAddToCart => {
    const { ocapi, setErrorAlert, setIsInventoryError } = usePageLayout();
    const { handleBasketCount } = useBasketCount();
    const { basket } = ocapi.data || {};

    const handleAddToCartAction = async (body?: ProductData) => {
        if (!basket || !body) {
            setErrorAlert(true);
            return null;
        }

        const {
            product_id: sku,
            inventory_id: inventoryId,
            quantity,
        } = body[0];

        const response = await handleAddToCart?.(body, basket.basket_id);

        if (response?.fault?.type === ITEM_NOT_AVAILALBLE_FAULT_TYPE) {
            console.error(
                `Inventory mismatch error. SKU: ${sku}, Quantity: ${quantity}, Inventory List: ${inventoryId}`
            );
            setIsInventoryError(true);
            setErrorAlert(true);
            return null;
        }

        if (!response || 'fault' in response) {
            console.error(
                `An error happened when trying to add to cart. Type: ${response?.fault?.type}, SKU: ${sku}`
            );
            setErrorAlert(true);
            return null;
        }

        const cartCount = getCartCount(response);

        if (cartCount) {
            handleBasketCount(cartCount);
        }

        return response as Basket;
    };

    return {
        handleAddToCartAction,
    };
};
